import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AdminProjectDocumentsService, AdminProjectDocument } from '../services/admin-project-documents.service';
import { useNotification } from '../context/NotificationContext';
import { resolveFileUrl } from '../utils/resolveFileUrl';
import styles from './AdminProjectDocumentsPage.module.css';

const statusOptions = [
  { value: 'UPLOADED', label: 'Cargado' },
  { value: 'IN_REVIEW', label: 'En revisión' },
  { value: 'OBSERVED', label: 'Observado/Rechazado' },
  { value: 'APPROVED', label: 'Aprobado' }
];

export default function AdminProjectDocumentsPage() {
  const { id } = useParams<{ id: string }>();
  const { addNotification } = useNotification();
  const [docs, setDocs] = useState<AdminProjectDocument[]>([]);
  const [loading, setLoading] = useState(true);

  const statusMeta = (status?: string) => {
    const key = (status || 'UPLOADED').toUpperCase();
    if (key === 'UPLOADED') return { label: 'Cargado', className: styles.statusUploaded };
    if (key === 'IN_REVIEW') return { label: 'En revisión', className: styles.statusInReview };
    if (key === 'OBSERVED') return { label: 'Observado', className: styles.statusObserved };
    if (key === 'APPROVED') return { label: 'Aprobado', className: styles.statusApproved };
    return { label: status || '---', className: styles.statusDefault };
  };

  const load = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await AdminProjectDocumentsService.list(id);
      const list = (res as any)?.data;
      setDocs(Array.isArray(list) ? list : []);
    } catch (error: any) {
      addNotification(error?.message || 'Error cargando documentos', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onUpdateStatus = async (docId: number, status: string) => {
    if (!id) return;
    try {
      await AdminProjectDocumentsService.updateStatus(id, docId, status);
      addNotification('Estado actualizado.', 'success');
      await load();
    } catch (error: any) {
      addNotification(error?.message || 'Error actualizando estado', 'error');
    }
  };

  if (loading) return <div className={styles.loading}>Cargando...</div>;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <p className={styles.eyebrow}>Documentación</p>
          <h1 className={styles.title}>Revisión documental</h1>
          <p className={styles.subtitle}>
            Evalúa los archivos cargados, valida su calidad y actualiza el estado.
          </p>
        </div>
        <div className={styles.headerActions}>
          <Link to="/admin/proyectos/documentos" className={styles.backButton}>
            Volver al listado
          </Link>
          <button className={styles.refreshButton} type="button" onClick={load}>
            Actualizar
          </button>
        </div>
      </header>

      <section className={styles.card}>
        {docs.length === 0 ? (
          <div className={styles.emptyState}>
            <h2 className={styles.emptyTitle}>No hay documentación cargada</h2>
            <p className={styles.emptyText}>Cuando el desarrollador suba archivos, los verás aquí.</p>
          </div>
        ) : (
          <div className={styles.docsGrid}>
            {docs.map((doc) => {
              const statusInfo = statusMeta(doc.status);
              return (
                <div key={doc.id} className={styles.docCard}>
                  <div className={styles.docHeader}>
                    <div>
                      <h3 className={styles.docTitle}>{doc.type.label}</h3>
                      <span className={`${styles.statusBadge} ${statusInfo.className}`}>
                        {statusInfo.label}
                      </span>
                    </div>
                    <div className={styles.actionGroup}>
                      <button
                        className={styles.approveButton}
                        type="button"
                        onClick={() => onUpdateStatus(doc.id, 'APPROVED')}
                      >
                        Aprobar
                      </button>
                      <button
                        className={styles.rejectButton}
                        type="button"
                        onClick={() => onUpdateStatus(doc.id, 'OBSERVED')}
                      >
                        Rechazar
                      </button>
                    </div>
                  </div>

                  <div className={styles.selectRow}>
                    <label className={styles.selectLabel}>Estado</label>
                    <select
                      className={styles.select}
                      value={doc.status}
                      onChange={(e) => onUpdateStatus(doc.id, e.target.value)}
                    >
                      {statusOptions.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className={styles.versions}>
                    {doc.versions?.length ? doc.versions.map((v) => (
                      <div key={v.id} className={styles.versionRow}>
                        <a href={resolveFileUrl(v.file_url)} target="_blank" rel="noreferrer" className={styles.versionLink}>
                          {v.file_name}
                        </a>
                        <span className={styles.versionBadge}>v{v.version}</span>
                      </div>
                    )) : (
                      <div className={styles.emptyVersions}>Sin versiones disponibles.</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
